import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTrackRestoreCustomField } from '@air/analytics';
import { Workspaces } from '@air/api';
import { type CustomField, type CustomFieldTypeName } from '@air/api/types';
import { Airror, NO_PERMISSION, NOT_FOUND } from '@air/errors';
import { useToasts } from '@air/provider-toast';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { RECENTLY_DELETED_KEY } from '~/components/RecentlyDeleted/hooks/queries/useRecentlyDeleted';
import { getWorkspaceCustomFieldsKey } from '~/constants/react-query-keys';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type RestoreCustomFieldParams = {
  customFieldId: CustomField['id'];
  customFieldName: CustomField['name'];
  customFieldType: CustomFieldTypeName;
};

export const useRestoreCustomField = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { trackRestoreCustomField } = useTrackRestoreCustomField();
  const { showToast } = useToasts();

  const mutation = useMutation({
    mutationFn: async ({ customFieldId }: RestoreCustomFieldParams) => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      return callAirApi(() => Workspaces.CustomFields.restore({ workspaceId, customFieldId }));
    },

    onSuccess: (_data, { customFieldId, customFieldName, customFieldType }) => {
      if (!workspaceId) {
        return;
      }

      trackRestoreCustomField({
        custom_field_id: customFieldId,
        custom_field_name: customFieldName,
        custom_field_type: customFieldType,
      });

      queryClient.invalidateQueries({
        queryKey: getWorkspaceCustomFieldsKey(workspaceId),
      });

      queryClient.invalidateQueries({
        queryKey: [RECENTLY_DELETED_KEY],
      });

      showToast('Custom field restored.');
    },

    onError: (error, { customFieldId, customFieldName }) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to restore custom field',
        metadata: {
          customFieldId,
          customFieldName,
        },
      });

      let message = 'Failed to restore custom field. Please try again.';

      if (error instanceof Airror) {
        if (error.statusCode === NOT_FOUND.statusCode) {
          message = 'Custom field not found or already restored.';
        } else if (error.statusCode === NO_PERMISSION.statusCode) {
          message = 'You do not have permission to restore this custom field.';
        }
      }

      showToast(message);
    },
  });

  return { restoreCustomField: mutation };
};
